import React, { useEffect, useMemo, useState } from 'react';
import ManagementModalLayout from '../ManagementModalLayout/ManagementModalLayout';
import ConfirmActionModal from '../ConfirmActionModal/ConfirmActionModal';
import './ManageUsersModal.css';
import type { LocalAuthUser, UserRole } from '../../data/types';
import type { ManagedUser } from '../../auth/roles';
import { canAssignRole, getAssignableRoles } from '../../auth/roles';
import { createUser, deleteUser, fetchUsers, updateUser } from '../../auth/userManagement';
import { useI18n } from '../../i18n/I18nContext';
import PasswordInput from '../PasswordInput/PasswordInput';

interface ManageUsersModalProps {
  currentUser: LocalAuthUser;
  onClose: () => void;
  showToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface UserDraft {
  id?: string;
  email: string;
  displayName: string;
  password: string;
  pin: string;
  isActive: boolean;
  role: UserRole;
}

const emptyDraft = (role: UserRole): UserDraft => ({
  email: '',
  displayName: '',
  password: '',
  pin: '',
  isActive: true,
  role,
});

const ManageUsersModal: React.FC<ManageUsersModalProps> = ({ currentUser, onClose, showToast }) => {
  const { t } = useI18n();
  const currentUserRole = currentUser.role;
  const assignableRoles = useMemo(() => getAssignableRoles(currentUser), [currentUserRole]);
  const defaultRole = assignableRoles[assignableRoles.length - 1] ?? 'USER';
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [draft, setDraft] = useState<UserDraft>(() => emptyDraft(defaultRole));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDeleteUsers = currentUser.role === 'SUPERADMIN' || currentUser.role === 'ADMIN';
  const canDeleteSelectedUser = Boolean(
    draft.id &&
      canDeleteUsers &&
      draft.id !== currentUser.id &&
      canAssignRole(currentUser.role, draft.role),
  );

  useEffect(() => {
    let cancelled = false;

    async function loadUsers() {
      setLoading(true);
      setError(null);

      try {
        const nextUsers = await fetchUsers();
        if (cancelled) {
          return;
        }
        setUsers(nextUsers);
        setSelectedUserId((currentSelectedUserId) =>
          currentSelectedUserId && nextUsers.some((user) => user.id === currentSelectedUserId)
            ? currentSelectedUserId
            : null,
        );
        setDraft((currentDraft) => {
          if (!currentDraft.id) {
            return assignableRoles.includes(currentDraft.role) ? currentDraft : emptyDraft(defaultRole);
          }

          const matchingUser = nextUsers.find((user) => user.id === currentDraft.id);
          if (!matchingUser) {
            return emptyDraft(defaultRole);
          }

          return currentDraft;
        });
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : t('users.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadUsers();

    return () => {
      cancelled = true;
    };
  }, [assignableRoles, defaultRole, t]);

  function selectUser(user: ManagedUser) {
    setSelectedUserId(user.id);
    setDraft({
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      password: '',
      pin: '',
      isActive: user.isActive,
      role: user.role,
    });
    setError(null);
  }

  function startCreateUser() {
    setSelectedUserId(null);
    setDraft(emptyDraft(defaultRole));
    setError(null);
  }

  async function reloadUsers(preferredId?: string | null) {
    const nextUsers = await fetchUsers();
    setUsers(nextUsers);

    const nextSelected =
      (preferredId ? nextUsers.find((user) => user.id === preferredId) : undefined) ??
      nextUsers[0];

    if (nextSelected) {
      selectUser(nextSelected);
      return;
    }

    setSelectedUserId(null);
    setDraft(emptyDraft(defaultRole));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);

    try {
      if (!draft.email.trim() || !draft.displayName.trim()) {
        throw new Error(t('users.requireFields'));
      }
      if (!draft.id && !draft.password.trim()) {
        throw new Error(t('users.requirePassword'));
      }
      if (!draft.id && !draft.pin.trim()) {
        throw new Error(t('users.requirePin'));
      }
      if (draft.pin.trim() && !/^\d{4,6}$/.test(draft.pin.trim())) {
        throw new Error(t('profile.invalidPin'));
      }

      const savedUser = draft.id
        ? await updateUser({
            id: draft.id,
          email: draft.email,
          displayName: draft.displayName,
          password: draft.password || undefined,
          pin: draft.pin || undefined,
          isActive: draft.isActive,
          role: draft.role,
        })
        : await createUser({
            email: draft.email,
            displayName: draft.displayName,
            password: draft.password,
            pin: draft.pin,
            isActive: draft.isActive,
            role: draft.role,
          });

      await reloadUsers(savedUser.id);
      showToast(draft.id ? t('users.updated') : t('users.created'), 'success');

      if (savedUser.id === currentUser.id) {
        showToast(t('users.reLoginInfo'), 'info');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('users.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!draft.id) {
      startCreateUser();
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await deleteUser(draft.id);
      setConfirmingDelete(false);
      await reloadUsers(selectedUserId === draft.id ? null : selectedUserId);
      showToast(t('users.deleted'), 'success');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('users.deleteError'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <ManagementModalLayout
      ariaLabel={t('users.title')}
      title={t('users.title')}
      closeAriaLabel={t('modal.closeManageUsers')}
      onClose={onClose}
      className="manage-users-modal"
      addButtonLabel={t('users.addUser')}
      addButtonAriaLabel={t('users.addUserAria')}
      onAdd={assignableRoles.length > 0 ? startCreateUser : undefined}
      sidebar={
        <>
          {loading ? (
            <div className="manage-users-loading">{t('users.loading')}</div>
          ) : users.length === 0 ? (
            <div className="manage-users-empty">{t('users.empty')}</div>
          ) : (
            <div className="management-modal-sidebar-scroll manage-users-list">
              {users.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className={[selectedUserId === user.id ? 'active' : '', user.isActive ? '' : 'inactive'].filter(Boolean).join(' ')}
                  onClick={() => selectUser(user)}
                >
                  <strong>{user.displayName}</strong>
                  <span>{user.email}</span>
                  <small>
                    {t(`roles.${user.role}`)} • {user.isActive ? t('users.active') : t('users.inactive')}
                  </small>
                </button>
              ))}
            </div>
          )}
        </>
      }
      form={
        <div className="manage-users-form">
          <div className="management-modal-form-header">
            <h4>{draft.id ? t('users.editUser') : t('users.createUser')}</h4>
            <label className="management-modal-toggle manage-users-active">
              <span>{t('users.active')}</span>
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
              />
            </label>
          </div>
          {error && <div className="manage-users-error">{error}</div>}

          <label>{t('users.email')}</label>
          <input
            type="email"
            value={draft.email}
            onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
          />

          <label>{t('users.displayName')}</label>
          <input
            type="text"
            value={draft.displayName}
            onChange={(event) => setDraft((current) => ({ ...current, displayName: event.target.value }))}
          />

          <label>{draft.id ? t('users.newPasswordOptional') : t('users.password')}</label>
          <PasswordInput
            value={draft.password}
            onChange={(value) => setDraft((current) => ({ ...current, password: value }))}
            usernameValue={draft.email}
            autoComplete="new-password"
          />

          <label>{draft.id ? t('users.newPinOptional') : t('users.pin')}</label>
          <PasswordInput
            inputMode="numeric"
            value={draft.pin}
            maxLength={6}
            onChange={(value) => setDraft((current) => ({ ...current, pin: value.replace(/\D/g, '').slice(0, 6) }))}
            usernameValue={draft.email}
            autoComplete="off"
          />

          <label>{t('users.role')}</label>
          <select
            value={draft.role}
            onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value as UserRole }))}
          >
            {assignableRoles.map((role) => (
              <option key={role} value={role}>
                {t(`roles.${role}`)}
              </option>
            ))}
          </select>

          <div className="manage-users-actions">
            <button type="button" className="secondary" onClick={onClose}>
              {t('users.close')}
            </button>
            {canDeleteSelectedUser ? (
              <button
                type="button"
                className="danger"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving || deleting}
              >
                {deleting ? t('users.deleting') : t('users.deleteButton')}
              </button>
            ) : null}
            <button type="button" className="primary" onClick={handleSave} disabled={saving || deleting}>
              {saving ? t('users.saving') : draft.id ? t('users.saveChanges') : t('users.createButton')}
            </button>
          </div>
        </div>
      }
      />
      {confirmingDelete && draft.id ? (
        <ConfirmActionModal
          title={t('confirm.deleteTitle')}
          message={t('confirm.deleteUserBody', { name: draft.displayName || draft.email })}
          confirmLabel={deleting ? t('users.deleting') : t('confirm.delete')}
          cancelLabel={t('confirm.cancel')}
          onConfirm={() => void handleDelete()}
          onCancel={() => {
            if (!deleting) {
              setConfirmingDelete(false);
            }
          }}
          loading={deleting}
        />
      ) : null}
    </>
  );
};

export default ManageUsersModal;
